import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isToday, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { projekteService } from '../services/projekteService';
import { auditsService } from '../services/auditsService';
import zeiteintraegeService from '../services/zeiteintraegeService';
import { AuditList, ProjektList, Zeiteintrag, ZeiteintragCreate } from '../types';
import { useNotification } from '../hooks/NotificationContext';
import { toLocalDateString } from '../utils/time';

/* ─── Helper ──────────────────────────────────────────────── */

const todayStr = () => toLocalDateString();

const weekRange = () => {
  const now = new Date();
  return {
    von: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    bis: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
  };
};

const monthRange = () => {
  const now = new Date();
  return {
    von: format(startOfMonth(now), 'yyyy-MM-dd'),
    bis: format(endOfMonth(now), 'yyyy-MM-dd'),
  };
};

/* ─── Summary-Card Komponente ─────────────────────────────── */

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  accentColor: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, accentColor }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const textPrimary = isDark ? '#e8edf3' : '#1a2332';
  const textSecondary = isDark ? '#8899aa' : '#5f7082';

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 30px ${alpha(accentColor, 0.12)}`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          bgcolor: accentColor,
          borderRadius: '12px 12px 0 0',
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.8rem' }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: alpha(accentColor, 0.08),
              borderRadius: '12px',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ─── Hauptseite ──────────────────────────────────────────── */

const ZeiterfassungPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canUnlinkAbrechnung = hasPermission(P.RECHNUNGEN_EDIT);
  const { showSuccess } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [projekte, setProjekte] = useState<ProjektList[]>([]);
  const [audits, setAudits] = useState<AuditList[]>([]);
  const [allAudits, setAllAudits] = useState<AuditList[]>([]);
  const [zeiteintraege, setZeiteintraege] = useState<Zeiteintrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state via URL-Params
  const selectedProjektId: number | '' = searchParams.get('projekt') ? Number(searchParams.get('projekt')) : '';
  const selectedAuditId: number | '' = searchParams.get('audit') ? Number(searchParams.get('audit')) : '';
  const datumVon = searchParams.get('von') || monthRange().von;
  const datumBis = searchParams.get('bis') || monthRange().bis;

  const setSelectedProjektId = (value: number | '') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) { next.set('projekt', String(value)); } else { next.delete('projekt'); }
      next.delete('audit');
      return next;
    }, { replace: true });
  };
  const setSelectedAuditId = (value: number | '') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) { next.set('audit', String(value)); } else { next.delete('audit'); }
      return next;
    }, { replace: true });
  };
  const setDatumVon = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== monthRange().von) { next.set('von', value); } else { next.delete('von'); }
      return next;
    }, { replace: true });
  };
  const setDatumBis = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== monthRange().bis) { next.set('bis', value); } else { next.delete('bis'); }
      return next;
    }, { replace: true });
  };

  const filterAudits = selectedProjektId
    ? allAudits.filter((a) => a.projekt_id === selectedProjektId)
    : allAudits;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Zeiteintrag | null>(null);
  const [formData, setFormData] = useState<ZeiteintragCreate>({
    datum: todayStr(),
    stunden: 8,
    beschreibung: '',
    mitarbeiter_id: 0,
    projekt_id: 0,
  });

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Zeiteintrag | null>(null);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<Zeiteintrag | null>(null);

  /* ─── Data loading ───────────────────────────────────────── */

  const loadProjekte = useCallback(async () => {
    if (!user) return;
    try {
      const data = await projekteService.list({
        projekt_typ: 'Kontingent',
        mitarbeiter_id: user.id,
      });
      setProjekte(data);
    } catch {
      setError('Fehler beim Laden der Projekte');
    }
  }, [user]);

  const loadZeiteintraege = useCallback(async () => {
    if (!user) return;
    try {
      const params: Record<string, unknown> = {
        mitarbeiter_id: user.id,
      };
      if (selectedProjektId) params.projekt_id = selectedProjektId;
      if (selectedAuditId) params.audit_id = selectedAuditId;
      if (datumVon) params.datum_von = datumVon;
      if (datumBis) params.datum_bis = datumBis;
      const data = await zeiteintraegeService.list(params as Parameters<typeof zeiteintraegeService.list>[0]);
      setZeiteintraege(data);
    } catch {
      setError('Fehler beim Laden der Zeiteinträge');
    }
  }, [user, selectedProjektId, selectedAuditId, datumVon, datumBis]);

  const loadAuditsForProjekt = useCallback(async (projektId: number) => {
    if (!projektId) {
      setAudits([]);
      return;
    }
    try {
      const data = await auditsService.list({ projekt_id: projektId });
      setAudits(data);
    } catch {
      // Keep dialog usable even if audits cannot be loaded
      setAudits([]);
    }
  }, []);

  const loadAllAudits = useCallback(async () => {
    try {
      const data = await auditsService.list();
      setAllAudits(data);
    } catch {
      setAllAudits([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadProjekte(), loadAllAudits()]);
      setLoading(false);
    };
    init();
  }, [loadProjekte, loadAllAudits]);

  useEffect(() => {
    if (!loading) {
      loadZeiteintraege();
    }
  }, [loadZeiteintraege, loading]);

  useEffect(() => {
    if (!dialogOpen || !formData.projekt_id) {
      return;
    }
    loadAuditsForProjekt(formData.projekt_id);
  }, [dialogOpen, formData.projekt_id, loadAuditsForProjekt]);

  /* ─── Summary calculations ───────────────────────────────── */

  const stundenHeute = zeiteintraege
    .filter((ze) => isToday(parseISO(ze.datum)))
    .reduce((s, ze) => s + ze.stunden, 0);

  const week = weekRange();
  const stundenWoche = zeiteintraege
    .filter((ze) => ze.datum >= week.von && ze.datum <= week.bis)
    .reduce((s, ze) => s + ze.stunden, 0);

  const month = monthRange();
  const stundenMonat = zeiteintraege
    .filter((ze) => ze.datum >= month.von && ze.datum <= month.bis)
    .reduce((s, ze) => s + ze.stunden, 0);

  const stundenGesamt = zeiteintraege.reduce((s, ze) => s + ze.stunden, 0);

  /* ─── Dialog handlers ────────────────────────────────────── */

  const openNewDialog = () => {
    const defaultProjektId = selectedProjektId || (projekte.length > 0 ? projekte[0].id : 0);
    setEditingEntry(null);
    setFormData({
      datum: todayStr(),
      stunden: 8,
      beschreibung: '',
      mitarbeiter_id: user?.id || 0,
      projekt_id: defaultProjektId,
      audit_id: undefined,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (entry: Zeiteintrag) => {
    setEditingEntry(entry);
    setFormData({
      datum: entry.datum,
      stunden: entry.stunden,
      beschreibung: entry.beschreibung || '',
      mitarbeiter_id: entry.mitarbeiter_id,
      projekt_id: entry.projekt_id,
      audit_id: entry.audit_id,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingEntry) {
        await zeiteintraegeService.update(editingEntry.id, {
          datum: formData.datum,
          stunden: formData.stunden,
          beschreibung: formData.beschreibung,
          projekt_id: formData.projekt_id,
          audit_id: formData.audit_id,
        });
        showSuccess('Eintrag aktualisiert');
      } else {
        await zeiteintraegeService.create({
          ...formData,
          mitarbeiter_id: user?.id || 0,
        });
        showSuccess('Eintrag erstellt');
      }
      setDialogOpen(false);
      loadZeiteintraege();
    } catch {
      // Error displayed by global axios interceptor
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await zeiteintraegeService.delete(deleteTarget.id);
      showSuccess('Eintrag gelöscht');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      loadZeiteintraege();
    } catch {
      // Error displayed by global axios interceptor
    }
  };

  const handleUnlinkAbrechnung = async () => {
    if (!unlinkTarget) return;
    try {
      await zeiteintraegeService.loeseAbrechnung(unlinkTarget.id);
      showSuccess('Abrechnungsmarkierung aufgehoben');
      setUnlinkConfirmOpen(false);
      setUnlinkTarget(null);
      loadZeiteintraege();
    } catch {
      // Error displayed by global axios interceptor
    }
  };

  const getProjektName = (projektId: number) => {
    const p = projekte.find((p) => p.id === projektId);
    return p ? p.name : `Projekt #${projektId}`;
  };

  const getAuditName = (auditId?: number) => {
    if (!auditId) return '-';
    const a = allAudits.find((audit) => audit.id === auditId);
    return a ? a.name : `Audit #${auditId}`;
  };

  /* ─── Quick filter presets ───────────────────────────────── */

  const setFilterThisWeek = () => {
    const w = weekRange();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('von', w.von);
      next.set('bis', w.bis);
      return next;
    }, { replace: true });
  };

  const setFilterThisMonth = () => {
    const m = monthRange();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Month is the default, so remove params for clean URL
      next.delete('von');
      next.delete('bis');
      return next;
    }, { replace: true });
  };

  /* ─── Render ─────────────────────────────────────────────── */

  if (loading) {
    return (
      <Box>
        <PageHeader title="Zeiterfassung" subtitle="Erfasse deine Arbeitszeiten auf Projekte" breadcrumbs={[{ label: 'Zeiterfassung' }]} />
        <TableSkeleton columns={5} rows={6} />
      </Box>
    );
  }

  if (projekte.length === 0 && !error) {
    return (
      <Box>
        <PageHeader title="Zeiterfassung" subtitle="Erfasse deine Arbeitszeiten auf Projekte" breadcrumbs={[{ label: 'Zeiterfassung' }]} />
        <Alert severity="info" sx={{ mt: 2 }}>
          Dir sind aktuell keine Kontingent-Projekte zugeordnet. Wende dich an deinen Projektleiter, um einem Projekt zugewiesen zu werden.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Zeiterfassung"
        subtitle="Erfasse deine Arbeitszeiten auf Projekte"
        breadcrumbs={[{ label: 'Zeiterfassung' }]}
        onAdd={openNewDialog}
        addLabel="Neuer Eintrag"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Summary Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Heute"
            value={`${stundenHeute.toFixed(1)}h`}
            icon={<TodayIcon sx={{ fontSize: 28 }} />}
            accentColor="#42a5f5"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Diese Woche"
            value={`${stundenWoche.toFixed(1)}h`}
            icon={<DateRangeIcon sx={{ fontSize: 28 }} />}
            accentColor="#3dd47e"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Dieser Monat"
            value={`${stundenMonat.toFixed(1)}h`}
            icon={<CalendarMonthIcon sx={{ fontSize: 28 }} />}
            accentColor="#ffa726"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Im Zeitraum"
            value={`${stundenGesamt.toFixed(1)}h`}
            icon={<AccessTimeIcon sx={{ fontSize: 28 }} />}
            accentColor="#ce93d8"
          />
        </Grid>
      </Grid>

      {/* ── Filter-Zeile ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Projekt</InputLabel>
                <Select
                  value={selectedProjektId}
                  onChange={(e) => setSelectedProjektId(e.target.value as number | '')}
                  label="Projekt"
                >
                  <MenuItem value="">
                    <em>Alle Projekte</em>
                  </MenuItem>
                  {projekte.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Audit</InputLabel>
                <Select
                  value={selectedAuditId}
                  onChange={(e) => setSelectedAuditId(e.target.value as number | '')}
                  label="Audit"
                >
                  <MenuItem value="">
                    <em>Alle Audits</em>
                  </MenuItem>
                  {filterAudits.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Von"
                value={datumVon}
                onChange={(e) => setDatumVon(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Bis"
                value={datumBis}
                onChange={(e) => setDatumBis(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={setFilterThisWeek}>
                  Woche
                </Button>
                <Button size="small" variant="outlined" onClick={setFilterThisMonth}>
                  Monat
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Tabelle ── */}
      {zeiteintraege.length === 0 ? (
        <Alert severity="info">Keine Zeiteinträge im gewählten Zeitraum gefunden.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Projekt</TableCell>
                <TableCell>Audit</TableCell>
                <TableCell align="right">Stunden</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell align="center">Abgerechnet</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zeiteintraege.map((ze) => (
                <TableRow key={ze.id}>
                  <TableCell>
                    {format(parseISO(ze.datum), 'EEE, dd.MM.yyyy', { locale: de })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      {getProjektName(ze.projekt_id)}
                    </Box>
                  </TableCell>
                  <TableCell>{getAuditName(ze.audit_id)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      component="span"
                      sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                    >
                      {ze.stunden.toFixed(1)}h
                    </Typography>
                  </TableCell>
                  <TableCell>{ze.beschreibung || '-'}</TableCell>
                  <TableCell align="center">
                    {ze.rechnung_id ? (
                      <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                        Ja
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Nein
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {ze.rechnung_id && canUnlinkAbrechnung && (
                        <Tooltip title="Abrechnungsmarkierung aufheben">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setUnlinkTarget(ze);
                              setUnlinkConfirmOpen(true);
                            }}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!ze.rechnung_id && (
                        <>
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => openEditDialog(ze)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteTarget(ze);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {zeiteintraege.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Gesamt: <strong>{stundenGesamt.toFixed(1)}h</strong>
            {' '}({zeiteintraege.length} {zeiteintraege.length === 1 ? 'Eintrag' : 'Einträge'})
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Nicht abgerechnet:{' '}
            <strong>
              {zeiteintraege
                .filter((ze) => !ze.rechnung_id)
                .reduce((s, ze) => s + ze.stunden, 0)
                .toFixed(1)}
              h
            </strong>
          </Typography>
        </Box>
      )}

      {/* ── Erstellen / Bearbeiten Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingEntry ? 'Eintrag bearbeiten' : 'Neuer Zeiteintrag'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Datum"
                value={formData.datum}
                onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Stunden"
                value={formData.stunden}
                onChange={(e) =>
                  setFormData({ ...formData, stunden: parseFloat(e.target.value) || 0 })
                }
                InputProps={{ inputProps: { min: 0.5, max: 24, step: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Projekt</InputLabel>
                <Select
                  value={formData.projekt_id || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, projekt_id: e.target.value as number, audit_id: undefined })
                  }
                  label="Projekt"
                >
                  {projekte.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Audit (optional)</InputLabel>
                <Select
                  value={formData.audit_id || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, audit_id: (e.target.value as number) || undefined })
                  }
                  label="Audit (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Audit</em>
                  </MenuItem>
                  {audits.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Beschreibung"
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                placeholder="Was hast du gemacht?"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.datum || !formData.stunden || !formData.projekt_id}
          >
            {editingEntry ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Loeschen Dialog ── */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Zeiteintrag löschen"
        message={
          deleteTarget
            ? `Möchtest du den Eintrag vom ${format(parseISO(deleteTarget.datum), 'dd.MM.yyyy')} (${deleteTarget.stunden}h) wirklich löschen?`
            : ''
        }
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
      />
      <ConfirmDialog
        open={unlinkConfirmOpen}
        title="Abrechnungsmarkierung aufheben"
        message={
          unlinkTarget
            ? `Die Markierung „abgerechnet“ für den Eintrag vom ${format(parseISO(unlinkTarget.datum), 'dd.MM.yyyy')} (${unlinkTarget.stunden}h) wird aufgehoben. Die Rechnung selbst bleibt unverändert.`
            : ''
        }
        onConfirm={handleUnlinkAbrechnung}
        onCancel={() => {
          setUnlinkConfirmOpen(false);
          setUnlinkTarget(null);
        }}
        confirmText="Aufheben"
      />
    </Box>
  );
};

export default ZeiterfassungPage;
